import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  aggregateSales,
  backfillOrders,
  cancelMonthRollover,
  listOrderDay,
  listPurchaseOrders,
  listTransferDay,
  previewMonthRollover,
  runNightly,
} from '../lib/queries.js';
import { useAuth } from '../auth/context.js';
import { Badge } from '../ui/Badge.js';

export default function Home() {
  const { me } = useAuth();
  const [orderDayCount, setOrderDayCount] = useState<number | null>(null);
  const [transferDayCount, setTransferDayCount] = useState<number | null>(null);
  const [poActive, setPoActive] = useState<number | null>(null);
  const [poStuck, setPoStuck] = useState<number | null>(null);
  const [runningNightly, setRunningNightly] = useState(false);
  const [nightlyOut, setNightlyOut] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      listOrderDay().then((r) => setOrderDayCount(r.total)).catch(() => setOrderDayCount(null)),
      listTransferDay().then((r) => setTransferDayCount(r.total)).catch(() => setTransferDayCount(null)),
      listPurchaseOrders('active').then((r) => setPoActive(r.total)).catch(() => setPoActive(null)),
      listPurchaseOrders('stuck').then((r) => setPoStuck(r.total)).catch(() => setPoStuck(null)),
    ]);
  }, []);

  const isSuper = me?.role === 'super_admin';

  return (
    <div className="space-y-6">
      <section>
        <h1 className="text-xl font-bold">สวัสดีคุณ {me?.name ?? ''}</h1>
        <p className="mt-1 text-sm text-slate-500">
          สรุปสถานะระบบวันนี้
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <Link
          to="/order-day"
          className="block rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-700 p-5 text-white shadow-sm transition hover:from-slate-800"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-300">สั่ง supplier วันนี้</div>
              <div className="mt-1 text-3xl font-bold">
                {orderDayCount === null ? '...' : `${orderDayCount} SKU`}
              </div>
              <div className="mt-1 text-sm text-slate-300">
                {orderDayCount === 0 ? 'ไม่มีต้องสั่ง 🎉' : 'รวม 4 สาขาใต้ min →'}
              </div>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </Link>

        <Link
          to="/transfer-day"
          className="block rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-700 to-emerald-500 p-5 text-white shadow-sm transition hover:from-emerald-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-emerald-50">โอนจาก bp วันนี้</div>
              <div className="mt-1 text-3xl font-bold">
                {transferDayCount === null ? '...' : `${transferDayCount} SKU`}
              </div>
              <div className="mt-1 text-sm text-emerald-50">
                {transferDayCount === 0 ? 'ไม่มีต้องโอน 🎉' : 'สาขาลูกใต้ min · bp พอ →'}
              </div>
            </div>
            <div className="text-4xl">🚚</div>
          </div>
        </Link>
      </section>

      <section className="grid grid-cols-2 gap-3">
        <StatCard
          to="/pos?status=active"
          label="PO กำลังดำเนินการ"
          value={poActive}
          tone="info"
        />
        <StatCard
          to="/pos?status=stuck"
          label="PO ค้าง"
          value={poStuck}
          tone={poStuck && poStuck > 0 ? 'danger' : 'neutral'}
        />
      </section>

      {isSuper && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">อัปเดตข้อมูล</h2>
              <p className="mt-1 text-xs text-slate-500">
                ปกติ cron รันให้เอง จันทร์/พุธ/ศุกร์ 19:00 — กดถ้าอยากให้ refresh ทันที
              </p>
            </div>
            <button
              onClick={async () => {
                setRunningNightly(true);
                setNightlyOut(null);
                try {
                  const r = await runNightly();
                  const summary = r.steps
                    .map((s) => `${s.step}: ${s.rows_affected}`)
                    .join(' · ');
                  setNightlyOut(`✅ ${summary}`);
                } catch (err) {
                  setNightlyOut('❌ ' + (err instanceof Error ? err.message : 'failed'));
                } finally {
                  setRunningNightly(false);
                }
              }}
              disabled={runningNightly}
              className="shrink-0 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {runningNightly ? '⏳ กำลังรัน...' : '🔄 รันทันที'}
            </button>
          </div>
          {nightlyOut && (
            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs">{nightlyOut}</div>
          )}

          <details className="mt-4 group">
            <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
              ▸ เครื่องมือเพิ่มเติม (ใช้เฉพาะตอน bootstrap หรือซ่อมข้อมูล)
            </summary>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  setRunningNightly(true);
                  setNightlyOut('กำลัง backfill sku/lot ของ order เก่า...');
                  try {
                    const r = await backfillOrders();
                    setNightlyOut(`backfill_order_skus: updated ${r.rows_updated} rows`);
                  } catch (err) {
                    setNightlyOut(err instanceof Error ? err.message : 'failed');
                  } finally {
                    setRunningNightly(false);
                  }
                }}
                disabled={runningNightly}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-60"
              >
                Backfill orders (ครั้งเดียว)
              </button>
              <button
                onClick={async () => {
                  setRunningNightly(true);
                  setNightlyOut('กำลัง aggregate sales...');
                  try {
                    const r = await aggregateSales();
                    setNightlyOut(`aggregate_sales: inserted ${r.rows_inserted} rows`);
                  } catch (err) {
                    setNightlyOut(err instanceof Error ? err.message : 'failed');
                  } finally {
                    setRunningNightly(false);
                  }
                }}
                disabled={runningNightly}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-60"
              >
                Aggregate sales
              </button>

              <button
                onClick={async () => {
                  setRunningNightly(true);
                  setNightlyOut(null);
                  try {
                    const preview = await previewMonthRollover();
                    if (preview.count === 0) {
                      setNightlyOut('ไม่มี PO ค้างจากเดือนก่อน — ไม่ต้องทำอะไร');
                      return;
                    }
                    if (
                      !confirm(
                        `จะ cancel PO ${preview.count} ใบที่ค้างจากเดือนก่อน — ยืนยัน?`
                      )
                    ) {
                      setNightlyOut('ยกเลิก — ไม่มีอะไรเปลี่ยน');
                      return;
                    }
                    const r = await cancelMonthRollover();
                    setNightlyOut(`cancel_rollover: cancelled ${r.rows_cancelled} POs`);
                  } catch (err) {
                    setNightlyOut(err instanceof Error ? err.message : 'failed');
                  } finally {
                    setRunningNightly(false);
                  }
                }}
                disabled={runningNightly}
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                title="ปกติ cron MWF จะตัดให้เอง — กดถ้าอยากทำทันที"
              >
                ✂️ ปิด PO เดือนข้ามทันที
              </button>
            </div>
          </details>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold">Stage 3 ใช้งานได้แล้ว</h2>
        <ul className="mt-3 space-y-1 text-sm text-slate-600">
          <li>• ค้นหา / ดู stock ข้ามสาขา</li>
          <li>• สร้าง PO พร้อมเตือน double-order</li>
          <li>• รับของแบบทยอย</li>
          <li>• โอนระหว่างสาขา</li>
        </ul>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/stock" className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">
            ไปหน้า Stock
          </Link>
          <Link to="/pos" className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">
            ดูรายการ PO
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatCard({
  to,
  label,
  value,
  tone,
}: {
  to: string;
  label: string;
  value: number | null;
  tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral';
}) {
  return (
    <Link
      to={to}
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300"
    >
      <div className="flex items-start justify-between">
        <div className="text-xs text-slate-500">{label}</div>
        <Badge tone={tone}>{value ?? '—'}</Badge>
      </div>
      <div className="mt-2 text-3xl font-bold">{value ?? '—'}</div>
    </Link>
  );
}
